import { useCallback, useMemo, useTransition } from 'react';
import { apm } from '@elastic/apm-rum';
import {
  useQueryClient,
  useSuspenseQuery,
  UseSuspenseQueryOptions,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import { useGlintsVIPMembershipInfo } from '../../components/GlintsVIPMembershipInfo/hooks/useGlintsVIPMembershipInfo';
import { getSessionCompanyId } from '../../selectors/session';
import { getUserId } from '../../selectors/user';
import { EmployerPurchaseProductType } from '../interfaces/entities';
import {
  CompanyFeature,
  CompanyFeatureConfig,
  DailyFeature,
  Feature,
  FeatureCount,
  JobFeatureCount,
  QuotaSource,
  ShortlistQuota,
} from '../interfaces/feature-count';
import { ReactQueryKeys } from '../react-query-keys';
import {
  consumeShortlistQuotaSources,
  exhaustCompanyQuotaSources,
} from './shortlistQuotaCache';
import { useAxiosWithAPI } from './useAxiosWithAPI';
import { useGetCompanyFreeTrialOffer } from './useGetCompanyFreeTrialOffer';
import useGetFeatureConfig from './useGetFeatureConfig';
import { useGetFeatureEnabledForCountry } from './useGetFeatureEnabledForCountry';
import { useGetSessionCompany } from './useGetSessionCompany';
import useIsMonthlyQuotaEnabled from './useIsMonthlyQuotaEnabled';

type FetchPolicy = 'cache-first' | 'network-only';

const configBasedOnFetchPolicy: Record<
  FetchPolicy,
  Pick<UseSuspenseQueryOptions<FeatureCount | null>, 'gcTime' | 'staleTime'>
> = {
  'cache-first': { gcTime: Infinity, staleTime: Infinity },
  'network-only': {
    gcTime: 0,
    staleTime: 0,
  },
};

export function useUpdateFeatureCountCache() {
  const queryClient = useQueryClient();
  const companyId = useSelector(getSessionCompanyId);
  const [, startTransition] = useTransition();

  type UpdateFeatureCountForShortListParams = {
    shortlistCount: number;
    forceLimitShortlist?: {
      limitDaily: boolean;
      limitMonthly?: boolean;
    };
    jobId?: string;
  };

  function updateFeatureCountForShortList({
    shortlistCount,
    forceLimitShortlist,
    jobId,
  }: UpdateFeatureCountForShortListParams) {
    if (!companyId || (!shortlistCount && !forceLimitShortlist)) return;
    startTransition(() => {
      const data = queryClient.getQueryData<FeatureCount>(
        ReactQueryKeys.featureCount(companyId)
      );
      const shortlist = data?.company?.daily?.SHORTLIST;
      if (!shortlist) return;

      const companyQuota = data?.company?.quotas?.SHORTLIST;
      let quotaDataUpdate: FeatureCount | null = null;
      let jobData: JobFeatureCount | undefined;
      let jobQuota: QuotaSource | undefined;

      if (jobId) {
        jobData = queryClient.getQueryData<JobFeatureCount>(
          ReactQueryKeys.jobFeatureCount(companyId, jobId)
        );
        jobQuota = jobData?.quotas?.SHORTLIST;
      }

      const quotaConsumption = consumeShortlistQuotaSources({
        shortlistCount,
        jobQuota,
        companyQuota,
      });

      if (jobId && jobData && quotaConsumption.jobQuota) {
        queryClient.setQueryData<JobFeatureCount>(
          ReactQueryKeys.jobFeatureCount(companyId, jobId),
          {
            ...jobData,
            quotas: {
              ...jobData.quotas,
              SHORTLIST: quotaConsumption.jobQuota,
            },
          }
        );
      }

      if (quotaConsumption.companyQuota) {
        quotaDataUpdate = getFeatureCountDataWithUpdatedQuota(
          data,
          quotaConsumption.companyQuota
        );
      }

      // A monthly-limited response means the company monthly shortlist quota is
      // exhausted. Force the cached quota so the quota tag, navbar badge, and
      // paywall reflect the limit immediately, without waiting for a refetch.
      // `companyQuota` only exists when the monthly quota feature is enabled.
      if (forceLimitShortlist?.limitMonthly && companyQuota) {
        const quotaData = quotaDataUpdate ?? data;
        const shortlistQuota =
          quotaData.company.quotas?.SHORTLIST ?? companyQuota;
        quotaDataUpdate = {
          ...quotaData,
          company: {
            ...quotaData.company,
            quotas: {
              ...quotaData.company.quotas,
              SHORTLIST: exhaustCompanyQuotaSources(shortlistQuota),
            },
          },
        };
      }

      const newDailyCount =
        shortlist.dailyCount + quotaConsumption.companyShortlistCount;

      const forceLimit = forceLimitShortlist?.limitDaily;
      const dataToUpdate: DailyFeature = forceLimit
        ? {
            ...shortlist,
            dailyCount: shortlist.dailyMax || 0,
            isDailyLimited: forceLimitShortlist?.limitDaily,
          }
        : {
            ...shortlist,
            dailyCount: newDailyCount,
            isDailyLimited: Boolean(
              shortlist.dailyMax && newDailyCount >= shortlist.dailyMax
            ),
            dailyMax: shortlist.dailyMax,
            nextLevelConfig: shortlist.nextLevelConfig,
          };

      queryClient.setQueryData<FeatureCount>(
        ReactQueryKeys.featureCount(companyId),
        {
          ...(quotaDataUpdate ?? data),
          company: {
            ...(quotaDataUpdate ?? data).company,
            daily: {
              ...(quotaDataUpdate ?? data).company.daily,
              SHORTLIST: dataToUpdate,
            },
          },
        }
      );
    });
  }
  return { updateFeatureCountForShortList } as const;
}

function getFeatureCountDataWithUpdatedQuota(
  data: FeatureCount,
  companyQuota: ShortlistQuota
): FeatureCount {
  return {
    ...data,
    company: {
      ...data.company,
      quotas: {
        ...data.company.quotas,
        SHORTLIST: {
          ...companyQuota,
        },
      },
    },
  };
}

export default function useGetFeatureCount(
  config: {
    fetchPolicy?: FetchPolicy;
  } = {}
) {
  const { fetchPolicy = 'cache-first' } = config;
  const additionalConfig = configBasedOnFetchPolicy[fetchPolicy];
  const axios = useAxiosWithAPI();
  const companyId = useSelector(getSessionCompanyId);
  const { isFreeSubscriptionPlan, hasCurrentCompanyJobSlotsSubscription } =
    useGetSessionCompany();
  const { isProfileLimitAccessEnabled } = useGetFeatureEnabledForCountry();
  const {
    vipMembershipInfo: {
      isActive: isVIPActive,
      trialInformationDetail: {
        isActive: isVIPTrialActive,
        isExtended: isVIPTrialExtended,
      },
    },
  } = useGlintsVIPMembershipInfo();

  const { setData: setFeatureConfig, data: featureConfig } =
    useGetFeatureConfig();
  const isMonthlyQuotaEnabled = useIsMonthlyQuotaEnabled();
  const companyFreeTrialOffer = useGetCompanyFreeTrialOffer({
    productType: EmployerPurchaseProductType.VIP_MEMBERSHIP,
    options: {
      fetchPolicy: 'cache-first',
    },
  });
  const hasFreeTrialOffer = Boolean(companyFreeTrialOffer?.id);

  const userId = useSelector(getUserId);
  const { data } = useSuspenseQuery({
    ...additionalConfig,
    queryKey: ReactQueryKeys.featureCount(companyId),
    queryFn: async () => {
      // Guard against missing or empty companyId to prevent 500 errors from /v2/feature/company
      if (
        !userId ||
        !companyId ||
        companyId === '' ||
        !isProfileLimitAccessEnabled
      ) {
        return null;
      }
      const { data } = await axios.get<{
        data: FeatureCount;
      }>(`/v2/featureCount`, {
        params: {
          companyId,
          companyDailyFeatures: [Feature.SHORTLIST],
          ...(isMonthlyQuotaEnabled
            ? { companyQuotaFeatures: [Feature.SHORTLIST] }
            : {}),
        },
        paramsSerializer: {
          indexes: null,
        },
      });

      if (!featureConfig) {
        const { data: configDataResponse } = await axios.get<{
          data: CompanyFeatureConfig;
        }>('/v2/feature/company', {
          params: {
            features: isMonthlyQuotaEnabled
              ? [CompanyFeature.SHORTLIST_DAILY, CompanyFeature.SHORTLIST_QUOTA]
              : [CompanyFeature.SHORTLIST_DAILY],
            companyId,
          },
          paramsSerializer: {
            indexes: null,
          },
        });
        setFeatureConfig(configDataResponse.data);
      }
      apm.setCustomContext({
        featureCount: {
          ...(data.data.company.daily?.SHORTLIST || {}),
        },
        shortlistQuota: {
          ...(data.data.company.quotas?.SHORTLIST || {}),
        },
      });
      return data.data;
    },
  });

  const companyDailyFeatureCount = data?.company.daily;
  const companyShortlistQuota = data?.company?.quotas?.SHORTLIST ?? null;
  const isMonthlyShortlistLimited = Boolean(
    companyShortlistQuota &&
      !companyShortlistQuota.isUnlimited &&
      companyShortlistQuota.totalRemaining <= 0
  );
  const isShortListLimited = isMonthlyQuotaEnabled
    ? isMonthlyShortlistLimited ||
      Boolean(companyDailyFeatureCount?.SHORTLIST?.isDailyLimited)
    : Boolean(companyDailyFeatureCount?.SHORTLIST?.isDailyLimited);

  const getNextLevelConfig = useCallback(
    (
      productType?:
        | typeof EmployerPurchaseProductType.VIP_MEMBERSHIP
        | typeof EmployerPurchaseProductType.PREMIUM_PLAN
    ) => {
      const vipConfig = isMonthlyQuotaEnabled
        ? featureConfig?.CUSTOM_VIP_LOCATION || featureConfig?.VIP
        : featureConfig?.VIP;
      const subscriptionConfig = isMonthlyQuotaEnabled
        ? featureConfig?.CUSTOM_SUBSCRIPTION_LOCATION ||
          featureConfig?.SUBSCRIPTION
        : featureConfig?.SUBSCRIPTION;

      if (productType === EmployerPurchaseProductType.VIP_MEMBERSHIP)
        return vipConfig;
      if (productType === EmployerPurchaseProductType.PREMIUM_PLAN)
        return subscriptionConfig;

      // user is in a paid subscription plan
      if (hasCurrentCompanyJobSlotsSubscription && !isFreeSubscriptionPlan)
        return null;

      if (isVIPTrialActive && !isVIPTrialExtended) return vipConfig;
      if (isVIPActive) return subscriptionConfig;

      return isMonthlyQuotaEnabled && hasFreeTrialOffer
        ? featureConfig?.CUSTOM_TRIAL_LOCATION || vipConfig
        : vipConfig;
    },
    [
      featureConfig,
      isMonthlyQuotaEnabled,
      hasCurrentCompanyJobSlotsSubscription,
      isFreeSubscriptionPlan,
      isVIPActive,
      isVIPTrialActive,
      isVIPTrialExtended,
      hasFreeTrialOffer,
    ]
  );

  const transformedCompanyDailyFeatureCount = useMemo(() => {
    if (!companyDailyFeatureCount?.SHORTLIST) return companyDailyFeatureCount;

    const dailyLimitResetTime = dayjs()
      .add(1, 'day')
      .startOf('day')
      .add(30, 'minutes')
      .toISOString();

    return {
      ...companyDailyFeatureCount,
      SHORTLIST: {
        ...companyDailyFeatureCount.SHORTLIST,
        dailyCount: companyDailyFeatureCount.SHORTLIST.dailyMax
          ? Math.min(
              companyDailyFeatureCount.SHORTLIST.dailyCount,
              companyDailyFeatureCount.SHORTLIST.dailyMax
            )
          : companyDailyFeatureCount.SHORTLIST.dailyCount,
        dailyLimitResetTime: dailyLimitResetTime,
        nextLevelConfig: getNextLevelConfig(),
      },
    };
  }, [companyDailyFeatureCount, getNextLevelConfig]);

  return {
    companyDailyFeatureCount: transformedCompanyDailyFeatureCount,
    companyShortlistQuota,
    isShortListLimited,
    isMonthlyShortlistLimited,
    getNextLevelConfig,
  } as const;
}
